import React, { useState, useEffect } from 'react';
import { Bell, BellOff, Clock, Check, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Helper: Convert Base64 VAPID to Uint8Array
const urlBase64ToUint8Array = (base64String) => {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  return new Uint8Array([...rawData].map((char) => char.charCodeAt(0)));
};

const PushNotificationSettings = ({ user }) => {
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [subscribing, setSubscribing] = useState(false);
  const [testingPush, setTestingPush] = useState(false);
  const [vapidKey, setVapidKey] = useState(null);
  const [preferences, setPreferences] = useState({
    streak_reminders: true,
    milestone_celebrations: true,
    comeback_messages: true,
    reminder_time: '19:00'
  });
  const [supported, setSupported] = useState(true);

  useEffect(() => {
    checkSupport();
    fetchStatus();
  }, []);

  const checkSupport = () => {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      setSupported(false);
      setLoading(false);
    }
  };

  const fetchStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Get VAPID key
      const keyResponse = await axios.get(`${API}/api/notifications/vapid-key`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVapidKey(keyResponse.data.publicKey);
      
      // Get subscription status
      const statusResponse = await axios.get(`${API}/api/notifications/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setIsSubscribed(statusResponse.data.subscribed);
      if (statusResponse.data.preferences) {
        setPreferences(statusResponse.data.preferences);
      }
    } catch (error) {
      console.error('Failed to fetch notification status:', error);
    } finally {
      setLoading(false);
    }
  };

  const registerServiceWorker = async () => {
    if ('serviceWorker' in navigator) {
      try {
        const registration = await navigator.serviceWorker.register('/sw-push.js');
        console.log('Service Worker registered:', registration);
        return registration;
      } catch (error) {
        console.error('Service Worker registration failed:', error);
        throw error;
      }
    }
    throw new Error('Service Workers not supported');
  };

  const subscribeToPush = async () => {
    if (!vapidKey) {
      toast.error('Configuração de notificações não disponível');
      return;
    }

    setSubscribing(true);
    try {
      // Request notification permission
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        toast.error('Permissão para notificações negada');
        setSubscribing(false);
        return;
      }

      // Register service worker
      const registration = await registerServiceWorker();
      await navigator.serviceWorker.ready;

      // Subscribe to push
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidKey)
      });

      // Send subscription to backend
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/notifications/subscribe`, subscription.toJSON(), {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      setIsSubscribed(true);
      toast.success('Notificações ativadas! Você receberá lembretes para manter sua sequência.', {
        icon: '🔔'
      });
    } catch (error) {
      console.error('Push subscription failed:', error);
      toast.error('Falha ao ativar notificações. Tente novamente.');
    } finally {
      setSubscribing(false);
    }
  };

  const unsubscribeFromPush = async () => {
    setSubscribing(true);
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/api/notifications/unsubscribe`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Also unsubscribe from browser
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      if (subscription) {
        await subscription.unsubscribe();
      }

      setIsSubscribed(false);
      toast.success('Notificações desativadas');
    } catch (error) {
      console.error('Unsubscribe failed:', error);
      toast.error('Falha ao desativar notificações');
    } finally {
      setSubscribing(false);
    }
  };

  const sendTestNotification = async () => {
    setTestingPush(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/notifications/test`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Notificação de teste enviada!');
    } catch (error) {
      console.error('Test notification failed:', error);
      toast.error('Falha ao enviar notificação de teste');
    } finally {
      setTestingPush(false);
    }
  };

  const updatePreferences = async (key, value) => {
    const newPreferences = { ...preferences, [key]: value };
    setPreferences(newPreferences);

    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/api/notifications/preferences`, newPreferences, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (error) {
      console.error('Failed to update preferences:', error);
      // Revert on error
      setPreferences(preferences);
    }
  };

  if (!supported) {
    return (
      <div className="bg-sand/50 rounded-2xl p-6">
        <div className="flex items-center gap-3 text-stone">
          <BellOff className="w-5 h-5" />
          <p className="text-sm">
            Notificações push não são suportadas neste navegador.
          </p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="bg-white rounded-2xl p-6 shadow-sm">
        <div className="flex items-center justify-center gap-2 text-stone">
          <Loader2 className="w-5 h-5 animate-spin" />
          <span>Carregando configurações...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl p-6 shadow-sm space-y-6" data-testid="push-notification-settings">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
            isSubscribed ? 'bg-lime/20' : 'bg-sand'
          }`}>
            <Bell className={`w-5 h-5 ${isSubscribed ? 'text-forest' : 'text-stone'}`} />
          </div>
          <div>
            <h3 className="font-semibold text-forest">Notificações Push</h3>
            <p className="text-xs text-stone">
              {isSubscribed ? 'Ativadas' : 'Desativadas'}
            </p>
          </div>
        </div>
        
        <Button
          onClick={isSubscribed ? unsubscribeFromPush : subscribeToPush}
          disabled={subscribing}
          variant={isSubscribed ? 'outline' : 'default'}
          className={!isSubscribed ? 'bg-forest hover:bg-forest/90 text-white' : ''}
          data-testid="toggle-push-btn"
        >
          {subscribing ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : isSubscribed ? (
            'Desativar'
          ) : (
            'Ativar Notificações'
          )}
        </Button>
      </div>

      {/* Subscription Benefits */}
      {!isSubscribed && (
        <div className="bg-lime/10 rounded-xl p-4">
          <p className="text-sm text-forest font-medium mb-2">
            Por que ativar notificações?
          </p>
          <ul className="space-y-2 text-sm text-stone">
            <li className="flex items-center gap-2">
              <Check className="w-4 h-4 text-lime" />
              <span>Lembretes para manter sua sequência</span>
            </li>
            <li className="flex items-center gap-2">
              <Check className="w-4 h-4 text-lime" />
              <span>Celebrações de marcos (7, 30, 100 dias)</span>
            </li>
            <li className="flex items-center gap-2">
              <Check className="w-4 h-4 text-lime" />
              <span>Mensagens personalizadas da Lira</span>
            </li>
          </ul>
        </div>
      )}

      {/* Preferences (only show if subscribed) */}
      {isSubscribed && (
        <div className="space-y-4 pt-4 border-t border-forest/10">
          <h4 className="font-medium text-forest text-sm">Preferências</h4>
          
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-forest">Lembretes de sequência</p>
                <p className="text-xs text-stone">Aviso quando sua sequência está em risco</p>
              </div>
              <Switch
                checked={preferences.streak_reminders}
                onCheckedChange={(checked) => updatePreferences('streak_reminders', checked)}
                data-testid="pref-streak-reminders"
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-forest">Celebrações de marcos</p>
                <p className="text-xs text-stone">Notificações ao atingir 7, 30, 100 dias</p>
              </div>
              <Switch
                checked={preferences.milestone_celebrations}
                onCheckedChange={(checked) => updatePreferences('milestone_celebrations', checked)}
                data-testid="pref-milestones"
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-forest">Mensagens de retorno</p>
                <p className="text-xs text-stone">A Lira te lembra se ficar ausente</p>
              </div>
              <Switch
                checked={preferences.comeback_messages}
                onCheckedChange={(checked) => updatePreferences('comeback_messages', checked)}
                data-testid="pref-comeback"
              />
            </div>

            {/* Reminder Time */}
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-forest">Horário do lembrete</p>
                <p className="text-xs text-stone">Quando você prefere ser lembrado</p>
              </div>
              <div className="flex items-center gap-2 bg-sand rounded-lg px-3 py-1.5">
                <Clock className="w-4 h-4 text-stone" />
                <select
                  value={preferences.reminder_time}
                  onChange={(e) => updatePreferences('reminder_time', e.target.value)}
                  className="bg-transparent text-sm text-forest focus:outline-none cursor-pointer"
                  data-testid="reminder-time-select"
                >
                  <option value="08:00">08:00</option>
                  <option value="12:00">12:00</option>
                  <option value="18:00">18:00</option>
                  <option value="19:00">19:00</option>
                  <option value="20:00">20:00</option>
                  <option value="21:00">21:00</option>
                </select>
              </div>
            </div>
          </div>

          {/* Test Button */}
          <Button
            onClick={sendTestNotification}
            disabled={testingPush}
            variant="outline"
            size="sm"
            className="w-full mt-4"
            data-testid="test-push-btn"
          >
            {testingPush ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                Enviando...
              </>
            ) : (
              <>
                <Bell className="w-4 h-4 mr-2" />
                Enviar Notificação de Teste
              </>
            )}
          </Button>
        </div>
      )}
    </div>
  );
};

export default PushNotificationSettings;
